import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

const logDiag = debug('rois:components:tables:work-vehicle-selection');
if (import.meta.env.DEV) {
  debug.enable('rois:components:tables:work-vehicle-selection*');
}

import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  Row,
  getFilteredRowModel,
} from '@tanstack/react-table';

// Styled Components
import {
  Container,
  SubContainer,
  SubContainerRight,
  ContainerTables,
  ContainerSearchBar,
  Table,
  Thead,
  Th,
  ThRightTable,
  Tr,
  TrBody,
  Td,
  TableRight,
  Tbody,
  TbodyRight,
  Img,
  Input,
  TdRightTable,
  StyledInput,
  FiltersContainer,
  DropDownFilterStyled,
} from './styles';

// Components
import { Typography } from '../../../Typography';
import SearchBar from '../../../../inputs/SearchBar';
import Badge from '../../../Badge';

// Icons
import ArrowIcon from '../../../../../assets/icons/grey-right-arrow-icon.svg';
import RemoveIcon from '../../../../../assets/icons/remove-file-icon.svg';

// Double tap row
import { DoubleTapRow } from '../../components/DoubleTapRow';

// Types
import {
  dataTable,
  tableProps,
  columnNames,
  getColumnId,
  onChangeInputTypes,
  variantThRight,
  variantThLeft,
} from './types';
import { OrderState } from '../../../../../types/orders';
import {
  VehicleOwnership,
  getVehicleOwnershipLabel,
} from '../../../../../types/vehicles';
import { Role } from '../../../../../types/auth';
import { useAuth } from '../../../../../hooks/useAuth';
import { OptionSelected } from '../../../../inputs/DropDown/DropDownList/types';
import { textSearchFilter } from '../../textSearchFilter';

export const WorkVehicleSelectionTable = ({
  data,
  teams,
  defaultValues = [],
  setValue,
  register,
  onAdd,
  onDelete,
  onUpdate,
  ...props
}: tableProps): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedRows, setSelectedRows] = useState<Row<dataTable>[]>([]);
  const [dataTableRight, setDataTableRight] =
    useState<dataTable[]>(defaultValues);
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(undefined);

  const dataTable = data?.vehicles ? data.vehicles : [];

  // 🔍 [DIAG] Log received vehicles to compare prod vs beta
  logDiag('dataTable recibido: %d %O', dataTable.length, dataTable);

  // Clear highlight after 1.5 seconds
  useEffect(() => {
    if (highlightedRowId) {
      const timer = setTimeout(() => setHighlightedRowId(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [highlightedRowId]);

  const columnHelper = createColumnHelper<dataTable>();

  // ✅ Usar ownership, remover usage (siempre es CONSTRUCTION)
  const handleClickRow = useCallback(
    (row: Row<dataTable>) => {
      logDiag('handleClickRow: añadiendo vehículo %O', { id: row.original.id, plate: row.original.plate });

      const newData: dataTable = {
        plate: row.original.plate,
        model: row.original.model,
        category: row.original.category,
        ownership: row.original.ownership,
        usage: row.original.usage,
        id: row.original.id,
      };

      const id = row.original.id;

      const setValues = () => {
        setValue(`vehiclesTable[${id}].plate`, row.original.plate);
        setValue(`vehiclesTable[${id}].model`, row.original.model);
        setValue(`vehiclesTable[${id}].category`, row.original.category);
        setValue(`vehiclesTable[${id}].ownership`, row.original.ownership);
        setValue(`vehiclesTable[${id}].usage`, row.original.usage);
        setValue(`vehiclesTable[${id}].id`, row.original.id);
        setValue(`vehiclesTable[${id}].vehicleId`, row.original.id);
      };

      const duplicate =
        selectedRows.some(
          (element) => element.original.id === row.original.id
        ) || dataTableRight.some((element) => element.id === row.original.id);

      logDiag('handleClickRow: duplicado=%s', duplicate);

      !duplicate && setValues();
      !duplicate && setDataTableRight([...dataTableRight, newData]);
      !duplicate && setSelectedRows([...selectedRows, row]);
      !duplicate && onAdd && onAdd(row.original.id as string);
    },
    [dataTableRight, selectedRows]
  );

  const removeRow = useCallback(
    (row: Row<dataTable>) => {
      logDiag('removeRow: borrando vehículo %O', { id: row.original.id, plate: row.original.plate });

      setDataTableRight((element) =>
        element.filter((item) => item.id !== row.original.id)
      );

      setSelectedRows((element) =>
        element.filter((item) => item.original.id !== row.original.id)
      );

      setValue(`vehiclesTable[${row.original.id}]`, undefined);

      onDelete &&
        onDelete(
          row.original.id as string,
          row.original.vehicleOccupantId as string
        );
    },
    [dataTableRight]
  );

  const columnsLeft = useMemo(
    () => [
      columnHelper.accessor('add' as any, {
        header: () => (
          <Typography colorText="white" size="S">
            +
          </Typography>
        ),
        cell: () => <Img src={ArrowIcon} />,
      }),
      columnHelper.accessor(getColumnId(columnNames.plate.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.plate.label}
          </Typography>
        ),
        cell: (info) => (
          <Typography size="S">{info.getValue() as string}</Typography>
        ),
        // Sin acentos y por palabras (antes 'includesString', solo esta
        // columna): el buscador mira también las columnas de texto que se ven.
        filterFn: textSearchFilter((row) => [row.plate, row.model, row.category]),
      }),
      columnHelper.accessor(getColumnId(columnNames.model.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.model.label}
          </Typography>
        ),
        cell: (info) => (
          <StyledInput value={info.getValue() as string} readOnly />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.category.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.category.label}
          </Typography>
        ),
        cell: (info) => (
          <StyledInput value={info.getValue() as string} readOnly />
        ),
      }),
      // ✅ Mostrar ownership
      columnHelper.accessor(getColumnId(columnNames.ownership.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.ownership.label}
          </Typography>
        ),
        cell: (info) => {
          const ownership = info.getValue() as string;
          const variant =
            ownership === VehicleOwnership.INTERNAL
              ? 'utility-internal'
              : 'utility-external';
          const label = getVehicleOwnershipLabel(ownership as VehicleOwnership);
          return (
            <Badge variant={variant} size="small">
              {label}
            </Badge>
          );
        },
      }),
      // Hidden column for team filtering - M2M: handles array of teams
      columnHelper.accessor((row) => row.teams, {
        id: 'team',
        header: () => 'Equipo',
        enableHiding: true,
        filterFn: (row, columnId, filterValue) => {
          const rowTeams = row.getValue(columnId) as string[] | undefined;
          if (!rowTeams || rowTeams.length === 0) return false; // Hide unassigned vehicles when filtering

          if (typeof filterValue === 'string') {
            return rowTeams.includes(filterValue);
          }
          if (Array.isArray(filterValue)) {
            return filterValue.some((teamName) => rowTeams.includes(teamName));
          }
          return false; // Default: hide vehicle if no criteria matches
        },
      }),
    ],
    [selectedRows]
  );

  const columnsRight = useMemo(
    () => [
      columnHelper.accessor(getColumnId(columnNames.plate.id), {
        header: () => (
          <Typography size="S" colorText="white">
            {columnNames.plate.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`vehiclesTable[${info.row.original.id}].plate`)}
            disabled={true}
            type="string"
          />
        ),
      }),
      columnHelper.accessor(getColumnId(columnNames.model.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.model.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`vehiclesTable[${info.row.original.id}].model`)}
            disabled={true}
            type="string"
          />
        ),
      }),
      // ✅ NUEVO: Mostrar categoría en tabla derecha
      columnHelper.accessor(getColumnId(columnNames.category.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.category.label}
          </Typography>
        ),
        cell: (info) => (
          <Input
            {...register(`vehiclesTable[${info.row.original.id}].category`)}
            disabled={true}
            type="string"
          />
        ),
      }),
      // ✅ Mostrar ownership (solo lectura en tabla derecha)
      columnHelper.accessor(getColumnId(columnNames.ownership.id), {
        header: () => (
          <Typography colorText="white" size="S">
            {columnNames.ownership.label}
          </Typography>
        ),
        cell: (info) => {
          const ownership = info.getValue() as string;
          const variant =
            ownership === VehicleOwnership.INTERNAL
              ? 'utility-internal'
              : 'utility-external';
          const label = getVehicleOwnershipLabel(ownership as VehicleOwnership);
          return (
            <Badge variant={variant} size="small">
              {label}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('remove' as any, {
        header: () => (
          <Typography colorText="white" size="S">
            -
          </Typography>
        ),
        cell: ({ row }) => (
          <Img
            src={RemoveIcon}
            onClick={() =>
              data?.parentExpeditionOrderStatus !== OrderState.Annulled &&
              data?.parentExpeditionOrderStatus !== OrderState.End &&
              data?.state !== OrderState.Annulled &&
              removeRow(row)
            }
          />
        ),
      }),
    ],
    [dataTableRight]
  );

  const table = useReactTable({
    data: dataTable,
    columns: columnsLeft,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      columnVisibility: {
        team: false,
      },
    },
  });

  const onSelectTeam = useCallback(
    (option: OptionSelected | undefined) => {
      setSelectedTeam(option?.value === '' ? undefined : option?.label);
      if (option?.value === '') {
        table.getColumn('team')?.setFilterValue(undefined);
      } else {
        table.getColumn('team')?.setFilterValue(option?.label);
      }
    },
    []
  );

  // Auto-selección de equipo para usuarios con rol Tablet/OperatingTeam
  useEffect(() => {
    const currentUserTeamId = user?.user?.teamIDs?.[0];
    const currentUserRole = user?.user?.rolActive;

    logDiag('auto-filtro equipo: %O', {
      rol: currentUserRole,
      teamID: currentUserTeamId || 'NO TIENE',
      teams: teams?.length || 0,
    });

    const rolesWithTeam = [Role.Tablet, Role.OperatingTeam];
    if (
      currentUserTeamId &&
      rolesWithTeam.includes(currentUserRole as Role) &&
      teams &&
      teams.length > 0
    ) {
      const userTeam = teams.find((t) => t.id === currentUserTeamId);
      logDiag('equipo encontrado: %O', userTeam ? userTeam.name : 'NO ENCONTRADO');
      if (userTeam) {
        table.getColumn('team')?.setFilterValue(userTeam.name);
        setSelectedTeam(userTeam.name);
      }
    }
  }, [teams, user?.user?.teamIDs, user?.user?.rolActive]);

  const tableRight = useReactTable({
    data: dataTableRight,
    columns: columnsRight,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const allLeftRows = table.getCoreRowModel().rows;
    const similarTables = allLeftRows.filter((row) => {
      const plate = row.original.plate;

      const matchingItem = dataTableRight.find((item) => item.plate === plate);

      if (matchingItem?.vehicleOccupantId) {
        row.original.vehicleOccupantId = matchingItem.vehicleOccupantId;
      }

      return !!matchingItem;
    });

    setSelectedRows(similarTables);
  }, []);

  const onChangeInput = useCallback<onChangeInputTypes>((value) => {
    const vehicle = table.getColumn(columnNames?.plate?.id);
    vehicle?.setFilterValue(value);
  }, []);

  return (
    <Container {...props}>
      <FiltersContainer>
        {teams && teams.length > 1 && (
          <DropDownFilterStyled
            key={`vehicle-team-filter-${selectedTeam || 'all'}`}
            inputSize={'XS'}
            onChangeSelected={(option) => onSelectTeam(option)}
            typeDropDown="Default"
            placeholder={t('forms:vehicles.team', 'Equipo')}
            defaultValue={selectedTeam}
            options={[
              {
                label: t('filters.all', 'Todos'),
                value: '',
              },
              ...(teams || []).map((team) => ({
                label: team.name,
                value: team.id,
              })),
            ]}
          />
        )}
        <ContainerSearchBar>
          <SearchBar
            variant="form"
            onChangeInput={(e) => onChangeInput(e.target.value)}
          />
        </ContainerSearchBar>
      </FiltersContainer>
      <ContainerTables>
        <SubContainer>
          <Table>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th variant={header.id as variantThLeft} key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isDisabled =
                  data?.parentExpeditionOrderStatus === OrderState.Annulled ||
                  data?.parentExpeditionOrderStatus === OrderState.End ||
                  data?.state === OrderState.Annulled;

                return (
                  <DoubleTapRow
                    key={row.id}
                    onSingleTap={() => !isDisabled && setHighlightedRowId(row.id)}
                    onDoubleTap={() => !isDisabled && handleClickRow(row)}
                    isDisabled={isDisabled}
                    StyledComponent={TrBody}
                    componentProps={{ $highlighted: highlightedRowId === row.id }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        variant={cell.id.slice(2) as variantThLeft}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    ))}
                  </DoubleTapRow>
                );
              })}
            </Tbody>
          </Table>
        </SubContainer>
        <SubContainerRight>
          <TableRight>
            <Thead>
              {tableRight.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <ThRightTable
                      variant={header.id as variantThRight}
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </ThRightTable>
                  ))}
                </Tr>
              ))}
            </Thead>
            <TbodyRight>
              {tableRight.getRowModel().rows.map((row) => (
                <TrBody key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TdRightTable
                      variant={cell.id.slice(2) as variantThRight}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TdRightTable>
                  ))}
                </TrBody>
              ))}
            </TbodyRight>
          </TableRight>
        </SubContainerRight>
      </ContainerTables>
    </Container>
  );
};
